import React, { useCallback, useState } from "react"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { useAppDispatch, useAppSelector } from "@hydrologiq/orders/state/hooks"
import {
  setOrdersWithPagination,
  setOrdersFilterOptions,
  setOrdersLoading,
  setOrdersError,
  getOrders,
  getOrdersPagination,
  getOrdersFilterOptions,
  getOrdersLoading,
  getOrdersError,
} from "@hydrologiq/orders/state/slice/ordersSlice"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { DEFAULT_PAGE_SIZE } from "@hydrologiq/shared/config"
export interface OrderFilters {
  [key: string]: string | undefined
  status?: string
  type?: string
  customer?: string
  site?: string
  asset?: string
  name?: string
  latest_event_status?: string
}

export interface OrderSort {
  field?: string
  direction?: "asc" | "desc"
}

// Default values
const DEFAULT_FILTERS: OrderFilters = {}
const DEFAULT_SORT: OrderSort | null = null

interface UsePaginatedOrdersReturn {
  orders: gqlTypes.Order[]
  pagination: gqlTypes.PaginationMeta | undefined
  filterOptions?: gqlTypes.OrderFilterOptions | undefined
  loading: boolean
  refreshing: boolean
  error: string | null
  filters: OrderFilters
  sort: OrderSort | null
  defaultFilters: OrderFilters
  defaultSort: OrderSort | null
  fetchOrders: (
    page?: number,
    pageSize?: number,
    newFilters?: OrderFilters,
    newSort?: OrderSort | null
  ) => Promise<void>
  setFilters: (filters: OrderFilters) => void
  setSort: (sort: OrderSort | null) => void
  clearFilters: () => void
  refetch: () => Promise<void>
}

export function usePaginatedOrders(
  initialPageSize: number = DEFAULT_PAGE_SIZE,
  skipInitialFetch: boolean = false
): UsePaginatedOrdersReturn {
  const dispatch = useAppDispatch()
  const orders = useAppSelector(getOrders)
  const pagination = useAppSelector(getOrdersPagination)
  const filterOptions = useAppSelector(getOrdersFilterOptions)
  const loading = useAppSelector(getOrdersLoading)
  const error = useAppSelector(getOrdersError)

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [filters, setFiltersState] = useState<OrderFilters>(DEFAULT_FILTERS)
  const [sort, setSortState] = useState<OrderSort | null>(DEFAULT_SORT)
  const [shouldFetch, setShouldFetch] = useState(!skipInitialFetch)

  // Build query variables
  const buildVariables = useCallback(
    (page: number, perPage: number, queryFilters: OrderFilters, querySort: OrderSort | null) => {
      const variables: any = {
        page,
        per_page: perPage,
      }

      // Add filter variables (only include non-empty values)
      if (queryFilters.status) variables.status = queryFilters.status
      if (queryFilters.type) variables.type = queryFilters.type
      if (queryFilters.customer) variables.customer = queryFilters.customer
      if (queryFilters.site) variables.site = queryFilters.site
      if (queryFilters.asset) variables.asset = queryFilters.asset
      if (queryFilters.name) variables.name = queryFilters.name
      if (queryFilters.latest_event_status) variables.latest_event_status = queryFilters.latest_event_status

      // Add sort variable
      if (querySort?.field && querySort?.direction) {
        variables.sort = `${querySort.field}:${querySort.direction}`
      }

      return variables
    },
    []
  )

  const {
    data,
    error: queryError,
    refetch: apolloRefetch,
    loading: apolloLoading,
    networkStatus,
  } = useQueryWithEndpoint(
    gqlTypes.GetOrdersDocument,
    {
      variables: buildVariables(currentPage, pageSize, filters, sort),
      fetchPolicy: "cache-first", // Use cached data first, refetch in background
      nextFetchPolicy: "cache-first", // Keep using cache-first for subsequent requests
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      skip: !shouldFetch, // Skip until shouldFetch is true
    },
    "primary"
  )

  // Update Redux state when Apollo data changes
  const updateReduxFromApollo = useCallback(() => {
    try {
      // Only show loading on initial load (networkStatus 1) or when variables change (networkStatus 2)
      // Don't show loading on refetch (networkStatus 4) to avoid flickering when switching tabs
      const isLoading = apolloLoading && (networkStatus === 1 || networkStatus === 2)
      dispatch(setOrdersLoading(isLoading))

      if (queryError) {
        dispatch(setOrdersError(queryError.message))
        return
      }

      dispatch(setOrdersError(null))

      if (data?.orders) {
        dispatch(
          setOrdersWithPagination({
            orders: data.orders.data as gqlTypes.Order[],
            pagination: data.orders.meta,
          })
        )

        // Update filter options if available
        if ((data as any).orders.filter_options) {
          dispatch(setOrdersFilterOptions((data as any).orders.filter_options))
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to process orders data"
      dispatch(setOrdersError(errorMessage))
    }
  }, [data, queryError, dispatch, apolloLoading, networkStatus])

  // Update Redux whenever Apollo data changes
  React.useEffect(() => {
    updateReduxFromApollo()
  }, [updateReduxFromApollo])

  const fetchOrders = useCallback(
    async (
      page: number = currentPage,
      perPage: number = pageSize,
      newFilters: OrderFilters = filters,
      newSort: OrderSort | null = sort
    ) => {
      try {
        dispatch(setOrdersLoading(true))
        dispatch(setOrdersError(null))

        // Update local state
        setCurrentPage(page)
        setPageSize(perPage)
        setFiltersState(newFilters)
        setSortState(newSort)

        // Enable fetching if it was skipped initially
        if (!shouldFetch) {
          setShouldFetch(true)
        }

        // Use Apollo refetch with new variables
        await apolloRefetch(buildVariables(page, perPage, newFilters, newSort))
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to load orders"
        dispatch(setOrdersError(errorMessage))
        dispatch(setOrdersLoading(false))
      }
    },
    [apolloRefetch, buildVariables, dispatch, currentPage, pageSize, filters, sort, shouldFetch]
  )

  const setFilters = useCallback(
    (newFilters: OrderFilters) => {
      // Update filter state immediately for responsive UI
      setFiltersState(newFilters)
      // Reset to page 1 when filters change
      fetchOrders(1, pageSize, newFilters, sort)
    },
    [fetchOrders, pageSize, sort]
  )

  const setSort = useCallback(
    (newSort: OrderSort | null) => {
      // Update sort state immediately for responsive UI
      setSortState(newSort)
      // Reset to page 1 when sorting changes
      fetchOrders(1, pageSize, filters, newSort)
    },
    [fetchOrders, pageSize, filters]
  )

  const clearFilters = useCallback(() => {
    // Restore default filters and sort
    setFiltersState(DEFAULT_FILTERS)
    setSortState(DEFAULT_SORT)
    fetchOrders(1, pageSize, DEFAULT_FILTERS, DEFAULT_SORT)
  }, [fetchOrders, pageSize])

  const refetch = useCallback(async () => {
    try {
      dispatch(setOrdersLoading(true))
      // Use Apollo's refetch with current variables - this ensures fresh data from server
      await apolloRefetch()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to refresh orders"
      dispatch(setOrdersError(errorMessage))
    } finally {
      dispatch(setOrdersLoading(false))
    }
  }, [apolloRefetch, dispatch])

  // Compute refreshing state - true when refetching in background (networkStatus 4)
  const refreshing = networkStatus === 4

  return {
    orders,
    pagination,
    filterOptions,
    loading,
    refreshing,
    error,
    filters,
    sort,
    defaultFilters: DEFAULT_FILTERS,
    defaultSort: DEFAULT_SORT,
    fetchOrders,
    setFilters,
    setSort,
    clearFilters,
    refetch,
  }
}
